import { useCallback } from "react";
import { FieldErrorContext } from "react-aria-components";
import { useController } from "react-hook-form";
import classnames from "../../../../../utils/classnames.js";
import { FieldError } from "../../../../design-system/forms/forms.js";
import MarkdownInput from "../../../../design-system/MarkdownInput/MarkdownInput.js";
import AnyFieldLabel from "../../AnyFieldLabel.js";
import * as cs from "../../RHFContentField.css.js";
import { useUiOptions } from "../../uiOptions.js";
import useFieldUiOptions from "../../useFieldUiOptions.js";
import type Props from "../Props.js";

export default function Markdown({
  typeDefinition,
  isNullable,
  isListItem,
  control,
  name,
  label,
  autoFocus,
}: Props) {
  const { isReadOnly } = useUiOptions();
  const { flexGrow } = useFieldUiOptions(name);
  const {
    field: { ref: fieldRef, ...field },
    fieldState,
  } = useController({ control, name });
  const fieldOnChange = field.onChange;
  const onChange = useCallback(
    (newValue: string) =>
      fieldOnChange(newValue === "" && isNullable ? null : newValue),
    [fieldOnChange, isNullable],
  );
  return (
    <div
      className={classnames(
        cs.StringField.Markdown.root,
        isListItem && cs.ListItemField.root,
        flexGrow && cs.Field.flexGrow,
      )}
      data-data-type={typeDefinition.dataType}
      data-is-list-item={isListItem}
      data-testid="widgets.RHFContentField.StringField.Markdown.root"
    >
      {!isListItem ? (
        <AnyFieldLabel
          name={field.name}
          typeDefinition={typeDefinition}
          isNullable={isNullable}
          label={label}
          htmlFor={field.name}
        />
      ) : null}
      <MarkdownInput
        value={field.value ?? ""}
        onChange={onChange}
        onBlur={field.onBlur}
        id={field.name}
        isInvalid={fieldState.invalid}
        isReadOnly={isReadOnly}
        placeholder="null"
        autoFocus={autoFocus}
        ref={fieldRef}
        className={flexGrow ? cs.Field.flexGrowContent : undefined}
      />
      <FieldErrorContext
        value={{
          isInvalid: fieldState.invalid,
          validationErrors: fieldState.error?.message
            ? [fieldState.error.message]
            : [],
          validationDetails: {} as ValidityState,
        }}
      >
        <FieldError>{fieldState.error?.message}</FieldError>
      </FieldErrorContext>
    </div>
  );
}
